import { removeHtmlTag } from '@exam-management/common/utils';
import { FormLeavingListener } from '@exam-management/components';
import {
  GetQuestionCreationTaskByIdDocument,
  GetTaskQuestionGroupByIdDocument,
  useSaveItemSetAsDraftWorkflowMutation,
  type GetTaskQuestionGroupByIdQuery,
} from '@exam-management/graphql';
import { type QBankStructureLevel } from '@exam-management/qbs-provider';
import { type ItemSetCreationFormInput } from '@exam-management/qcontent-management';
import { parseContentForApi } from '@exam-management/rich-text-editor';
import { useParams } from '@exam-management/router';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form } from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import type { PropsWithChildren } from '@shared/utils';
import { useCallback, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

import { useParseContentForItemSetQuestionCreation } from '../../hooks';
const ITEM_SET_NAME_MAX_LENGTH = 256;

const DEFAULT_ITEM_SET_FORM_VALUES = {
  details: {
    name: '',
    paragraph: '',
  },
};

interface ItemSetQuestionCreationFormProps extends PropsWithChildren {
  qbsLevels: QBankStructureLevel[];
  size: number;
  questionGroupData: GetTaskQuestionGroupByIdQuery['qctGetTaskQuestionGroupById'];
}

export default function ItemSetQuestionEditForm(props: Readonly<ItemSetQuestionCreationFormProps>) {
  const { children, questionGroupData } = props;
  const { showSnackbar } = useSnackbar();
  const { taskId } = useParams('/my-task/question-creation/:taskId/item-set/:questionId');
  const [saveItemSetAsDraftWorkflow] = useSaveItemSetAsDraftWorkflowMutation({
    onError: () => {
      showSnackbar({
        message: 'Unable to save Question',
        variant: 'error',
      });
    },
  });

  const editItemSetFormSchema = z.object({
    details: z.object({
      name: z
        .string()
        .trim()
        .min(1, { message: 'กรุณากรอกชื่อ item set' })
        .max(ITEM_SET_NAME_MAX_LENGTH, {
          message: `ชื่อ item set ต้องมีความยาวไม่เกิน ${ITEM_SET_NAME_MAX_LENGTH} ตัวอักษร`,
        }),
      paragraph: z.string().refine(
        (value) => {
          const details = removeHtmlTag(value, {
            customTag: 'p',
          }).trim();
          return details.length >= 1;
        },
        {
          message: 'กรุณากรอกบทความ',
        },
      ),
    }),
  });

  const { parseItemSetQuestionContent } = useParseContentForItemSetQuestionCreation({
    taskId: taskId,
    questionGroupId: questionGroupData.id,
  });

  const formContext = useForm<ItemSetCreationFormInput>({
    defaultValues: {
      details: {
        name: questionGroupData?.name ?? DEFAULT_ITEM_SET_FORM_VALUES.details.name,
        paragraph:
          questionGroupData?.metadata?.paragraph ?? DEFAULT_ITEM_SET_FORM_VALUES.details.paragraph,
      },
    },
    resolver: zodResolver(editItemSetFormSchema),
  });

  const handleResetForm = useCallback(async () => {
    formContext.reset({
      details: {
        name: questionGroupData?.name ?? DEFAULT_ITEM_SET_FORM_VALUES.details.name,
        paragraph:
          (questionGroupData?.metadata?.paragraph
            ? await parseItemSetQuestionContent(questionGroupData.metadata.paragraph)
            : DEFAULT_ITEM_SET_FORM_VALUES.details.paragraph) ?? '',
      },
    });
  }, [formContext, questionGroupData, parseItemSetQuestionContent]);

  useEffect(() => {
    handleResetForm();
  }, [handleResetForm]);

  const handleItemSetSaveFormSubmit = async (data: ItemSetCreationFormInput) => {
    await saveItemSetAsDraftWorkflow({
      variables: {
        input: {
          questionGroupId: questionGroupData?.id ?? '',
          paragraph: parseContentForApi(data?.details?.paragraph ?? ''),
          name: data?.details?.name ?? '',
        },
        taskId,
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: GetQuestionCreationTaskByIdDocument,
          variables: {
            getTaskByIdInput: {
              taskId,
            },
          },
        },
        {
          query: GetTaskQuestionGroupByIdDocument,
          variables: {
            questionGroupId: questionGroupData?.id ?? '',
            taskId,
          },
        },
      ],
      onCompleted: () => {
        showSnackbar({
          message: 'Question has been saved',
          variant: 'success',
        });
      },
    });
  };

  return (
    <FormProvider {...formContext}>
      <Form onSubmit={formContext.handleSubmit(handleItemSetSaveFormSubmit)}>{children}</Form>
      <FormLeavingListener onSubmit={handleItemSetSaveFormSubmit} />
    </FormProvider>
  );
}
